/* eslint-disable no-unused-vars */
import { useEffect, useMemo, useState } from "react";
import { Box, Card, Grid, IconButton, Menu, MenuItem, Tooltip, Typography, Button, Dialog, DialogContent, DialogTitle, Paper, } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Link, useLocation, useNavigate, useParams, } from "react-router-dom";
import { MdOutlineWatchLater } from "react-icons/md";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CustomTable from "../../../components/customTable/customTable";
import { update } from "../../../slice/jobSlice";
import { setControlAction, setControlUser, setPortalControlBy } from "../../../slice/userSlice";
import { API_URL } from "../../../appConstants";
import { useTMutation } from "../../../utils/useTMutation";
import showToast from "../../../components/Hot-Toast/showToast";
import IMAGE from "../../../assets/images/big/abcz.png";
import JobDetails from "../../CommonViewPages/clinicianAppliedJobDetails";
import CloseIcon from "@mui/icons-material/Close";


const ViewJobInFacility = () => {
    let view = false;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const { jobId, clinicianId } = useParams();
    const [event, setEvent] = useState({})
    const { jobData } = useSelector(state => state.job);
    const { userDetails } = useSelector(state => state.user);
    const [anchorEl, setAnchorEl] = useState(null);
    const { mutateAsync } = useTMutation("/job/addUpdateJob", "post", (data => { return data }));
    const jobMutation = useTMutation("/job/viewJobDetailsForViewOffer", "post", (jobResult) => { return JobDetails });
    const { mutateAsync: fetchFullJobData } = useTMutation("/job/viewJob", "post", (jobResult) => { return jobResult });


    // Extract query params from URL
    const queryParams = new URLSearchParams(location.search);
    const shouldFetch = queryParams.get("navigate") == "true";

    // Fetch job details when conditions are met
    useEffect(() => {
        if (shouldFetch && jobId && clinicianId) {
            fetchJobDetails();
        }
    }, [shouldFetch, jobId, clinicianId]);

    // Ensure jobData always has default values for critical fields
    // This handles both API-fetched and Redux-loaded data
    useEffect(() => {
        if (jobData && Object.keys(jobData).length > 0) {
            const enrichedJobData = {
                ...jobData,
                REQUIRED_APPLICANTS: jobData?.REQUIRED_APPLICANTS ?? "1",
                JOB_DESCRIPION: jobData?.JOB_DESCRIPION ?? "",
                JOB_RESPONSIBILITY: jobData?.JOB_RESPONSIBILITY ?? ""
            };
            // Only update if data was enriched (missing fields)
            if (enrichedJobData.REQUIRED_APPLICANTS !== jobData.REQUIRED_APPLICANTS ||
                enrichedJobData.JOB_DESCRIPION !== jobData.JOB_DESCRIPION ||
                enrichedJobData.JOB_RESPONSIBILITY !== jobData.JOB_RESPONSIBILITY) {
                dispatch(update(enrichedJobData));
            }
        }
    }, [jobData, dispatch]);

    const fetchJobDetails = async () => {
        try {
            // First, fetch clinician-job relationship data
            const clinicianJobResponse = await jobMutation.mutateAsync({ jobId, clinicianId });
            
            if (clinicianJobResponse.STATUS && clinicianJobResponse.OUTPUT?.length > 0) {
                const clinicianJobData = clinicianJobResponse.OUTPUT[0];
                const fullJobId = clinicianJobData?.jobID;
                
                // Now fetch the complete job details using the jobID
                if (fullJobId) {
                    try {
                        const fullJobResponse = await fetchFullJobData({ ID: fullJobId });
                        
                        if (fullJobResponse?.STATUS && fullJobResponse?.OUTPUT?.length > 0) {
                            const fullJobData = fullJobResponse.OUTPUT[0];
                            
                            // Merge clinician-job data with full job data
                            // Full job data takes priority for job-specific fields
                            const mergedJobData = {
                                ...clinicianJobData,
                                ...fullJobData,
                                // Preserve clinician data from the first response
                                clinician: clinicianJobData?.clinician,
                                clinicianStatus: clinicianJobData?.clinicianStatus,
                                offerStatus: clinicianJobData?.offerStatus
                            };
                            
                            // Ensure all required fields have defaults
                            const completedJobData = {
                                ...mergedJobData,
                                REQUIRED_APPLICANTS: mergedJobData?.REQUIRED_APPLICANTS ?? "1",
                                JOB_DESCRIPION: mergedJobData?.JOB_DESCRIPION ?? "",
                                JOB_RESPONSIBILITY: mergedJobData?.JOB_RESPONSIBILITY ?? ""
                            };
                            
                            dispatch(update(completedJobData));
                        } else {
                            // If full job fetch fails, use the clinician-job data with defaults
                            const completedJobData = {
                                ...clinicianJobData,
                                REQUIRED_APPLICANTS: clinicianJobData?.REQUIRED_APPLICANTS ?? "1",
                                JOB_DESCRIPION: clinicianJobData?.JOB_DESCRIPION ?? "",
                                JOB_RESPONSIBILITY: clinicianJobData?.JOB_RESPONSIBILITY ?? ""
                            };
                            dispatch(update(completedJobData));
                        }
                    } catch (fullJobError) {
                        console.error("Error fetching full job details:", fullJobError);
                        // Fallback to clinician-job data if full job fetch fails
                        const completedJobData = {
                            ...clinicianJobData,
                            REQUIRED_APPLICANTS: clinicianJobData?.REQUIRED_APPLICANTS ?? "1",
                            JOB_DESCRIPION: clinicianJobData?.JOB_DESCRIPION ?? "",
                            JOB_RESPONSIBILITY: clinicianJobData?.JOB_RESPONSIBILITY ?? ""
                        };
                        dispatch(update(completedJobData));
                    }
                } else {
                    // If no jobID found, use clinician-job data with defaults
                    const completedJobData = {
                        ...clinicianJobData,
                        REQUIRED_APPLICANTS: clinicianJobData?.REQUIRED_APPLICANTS ?? "1",
                        JOB_DESCRIPION: clinicianJobData?.JOB_DESCRIPION ?? "",
                        JOB_RESPONSIBILITY: clinicianJobData?.JOB_RESPONSIBILITY ?? ""
                    };
                    dispatch(update(completedJobData));
                }
            } else {
                showToast("error", clinicianJobResponse.MESSAGE || "Failed to fetch job details", 3000);
            }
        } catch (error) {
            console.error("Error fetching job details:", error);
            showToast("error", "Something went wrong, Please try again", 3000);
        }
    };

    const handleApplicantStatus = (row) => {
        const clinicianID = row?.clinician?.ID;
        const clinicianInFacilityID = row?.ID;
        const clinicianStatus = row?.clinicianStatus;
        dispatch(setPortalControlBy('facility'));
        dispatch(setControlAction("view"));
        dispatch(setControlUser(clinicianID));
        navigate(`/facility/clinican-users/${clinicianID}/${clinicianInFacilityID}/${clinicianStatus}`);
    }

    let col = [
        {
            name: 'CLINICIAN',
            selector: row => row?.clinician?.FULL_NAME,
            reorder: true,
            sortable: true,

        },
        {
            name: 'ROLE',
            selector: row => row.clinician?.jobRole?.ROLE_NAME,
            reorder: true,
            sortable: true,

        },
        {
            name: 'JOB STATUS',
            selector: row => row.clinicianStatus,
            reorder: true,
            sortable: true,

        },
        {
            name: 'ACTIONS',
            button: true,
            cell: row => (
                <div style={{ paddingLeft: "15px" }}>
                    <Tooltip title="View Application">
                        <IconButton size="small"
                            onClick={() => handleApplicantStatus(row)}
                        >
                            <MoreHorizIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            ),
            reorder: true,
            center: true
        }

    ]
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleDropdown = async (val) => {
        if (typeof val == "string") {
            try {
                const response = await mutateAsync({ ID: jobData?.ID, JOB_STATUS: val });
                if (response.STATUS) {
                    dispatch(update({ JOB_STATUS: val }))
                    showToast("success", `Application Status Changed to ${val} `, 3000);
                } else {
                    showToast("error", response.MESSAGE, 3000);
                }
            } catch (error) {
                console.error("Menu option error:", error.message);
            }
        }
        setAnchorEl(null);
    };
    const payload = useMemo(() => ({
        JOB_ID: jobData?.ID,
        SPECIALTY_ID: jobData?.SPECIALTY_ID,
        // ...(jobData?.checkStatus === "offerAccept" && { CHECKSTATUS: "OfferAccepted" })
    }), [jobData]);
    // Prevent rendering if jobData is still empty
    if (!jobData || Object.keys(jobData).length === 0) {
        return <div>Loading job details...</div>;
    }

    return (
        <>
            {Object.keys(jobData).length > 0 && <>
                <Grid container >
                    <Box onClick={() => navigate(-1)} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <IconButton >
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography variant="body1"  >
                            Back
                        </Typography>
                    </Box>

                    <Grid item lg={12}>
                        <Card>
                            <Grid container lg={12}>

                                <Grid item lg={6} p={2}>
                                    <Box>
                                        <Typography variant="h2" pt={0} style={{ fontWeight: 600, fontSize: "20px", color: "#000000", opacity: "87%" }}>{jobData?.jobs_role?.ROLE_NAME}</Typography>
                                        <Typography variant="h4" pt={2} style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>{new Date(jobData?.SERVICE_DATE).toDateString()}</Typography>

                                        {jobData?.SERVICE_TYPE === "Recurring" ?
                                            <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2} width="100%">
                                                {jobData?.SERVICE_DURATION?.map((job, index) => (
                                                    <Box
                                                        key={index}
                                                        display="flex"
                                                        flexDirection="row"
                                                        flexShrink={0}
                                                        width="250px"
                                                    >
                                                        <Typography
                                                            variant="h2"
                                                            pt={1}
                                                            style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}
                                                        >
                                                            <MdOutlineWatchLater />
                                                        </Typography>
                                                        <Typography
                                                            pt={1}
                                                            px={1}
                                                            variant="h2"
                                                            style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}
                                                        >
                                                            {`${job?.JOB_DAY?.slice(0, 3)} ${job?.JOB_IN_TIME}-${job?.JOB_OUT_TIME}`}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                            : <>

                                                <Box display="flex" gap={1}>
                                                    <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}><MdOutlineWatchLater /></Typography>
                                                    <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{new Date(jobData?.job_services[0]?.JOB_IN_TIME).toLocaleTimeString()}</Typography>  <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>-</Typography>
                                                    <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{new Date(jobData?.job_services[0]?.JOB_OUT_TIME).toLocaleTimeString()}</Typography>
                                                </Box>
                                            </>}

                                        <Typography variant="h4" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>Glendora, CA</Typography>
                                    </Box>
                                    <Box display="flex" gap={3} mt={2} justifyContent="start">
                                        {jobData?.jobs_role?.ROLE_NAME && <Typography style={{ backgroundColor: "#eef7e9", borderRadius: "20px", padding: "10px 20px", color: 'linear-gradient(to right,#4eb3e8,#6ebe44)', fontWeight: 500, fontSize: "14px" }}>{jobData?.jobs_role?.ROLE_NAME}</Typography>}
                                        {jobData?.JOB_TYPE && <Typography style={{ backgroundColor: "#eef7e9", borderRadius: "20px", padding: "10px 20px", color: 'linear-gradient(to right,#4eb3e8,#6ebe44)', fontWeight: 500, fontSize: "14px" }}>{jobData?.JOB_TYPE}</Typography>}
                                        {jobData?.SERVICE_TYPE && <Typography style={{ backgroundColor: "#eef7e9", borderRadius: "20px", padding: "10px 20px", color: 'linear-gradient(to right,#4eb3e8,#6ebe44)', fontWeight: 500, fontSize: "14px" }}>{jobData?.SERVICE_TYPE}</Typography>}
                                    </Box>
                                </Grid>
                                <Grid lg={6} item>
                                    {jobData?.checkStatus !== "offerAccept" &&
                                        <Box display="flex" justifyContent="end" p={2}>
                                            <Grid mt={1}>
                                                <Typography style={{ backgroundColor: jobData?.JOB_STATUS === "Clinician Hired" ? 'linear-gradient(to right,#4eb3e8,#6ebe44)' : "#f6b613", borderRadius: "20px", padding: "1px 20px", color: "#ffffff", fontWeight: 500, fontSize: "14px" }}>{jobData?.JOB_STATUS}</Typography>
                                            </Grid>
                                            <IconButton p={0} onClick={handleClick}>
                                                <MoreHorizIcon />
                                            </IconButton>

                                        </Box>}
                                    <Menu
                                        id="notification-menu"
                                        anchorEl={anchorEl}
                                        keepMounted
                                        open={Boolean(anchorEl)}
                                        onClose={handleDropdown}
                                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                                        sx={{
                                            "& .MuiMenu-paper": {
                                                width: "200px",
                                                right: 0,
                                                top: "70px !important",
                                            },
                                        }}
                                    >
                                        <MenuItem onClick={() => handleDropdown("Open for Applications")}>Open for Applications</MenuItem>
                                        <MenuItem onClick={() => handleDropdown("Candidates Selected")}>Candidates Selected</MenuItem>
                                        <MenuItem onClick={() => handleDropdown("Job Closed")}>Job Closed</MenuItem>
                                        <MenuItem onClick={() => handleDropdown("Draft Application")}>Edit Job Posting</MenuItem>
                                    </Menu>
                                </Grid>

                            </Grid>
                        </Card>
                    </Grid>
                </Grid>
                <Grid mt={2}>
                    <CustomTable
                        column={col}
                        event={(e) => { setEvent(e) }}
                        url={"/job/fetchInterstedClinician"}
                        headline={"Applicant"}
                        payload={payload}
                    />

                </Grid>
                <Grid container spacing={1} justifyContent="space-around" mt={2} pb={18}>
                    <Grid item lg={4} pb={2}>
                        <Card  >
                            <Grid container alignItems="center" spacing={1} justifyContent="space-around" px={3} pb={3}>
                                <Grid item xs={12} sm={12} display="flex" gap={3}>
                                    <Typography pt={2}>
                                        <img
                                            src={jobData?.user?.PROFILE_IMAGE_PATH
                                                ? API_URL + "user/viewFile?fileName=" + jobData?.user?.PROFILE_IMAGE_PATH
                                                : IMAGE}
                                            alt="profile"
                                            style={{ width: '100px' }}
                                        />
                                    </Typography>
                                    <Typography>
                                        <Box pt={2}>
                                            <Typography style={{ fontWeight: 500, fontSize: "14px", color: "#000000", opacity: "87%" }}>{userDetails?.FACILITY_NAME}</Typography>
                                            <Typography style={{ fontWeight: 400, fontSize: "12px", color: "#000000", opacity: "60%" }}>{userDetails?.STREET_ADDRESS}</Typography>
                                        </Box>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Box>
                                        <Typography variant="h2" pt={2} style={{ fontWeight: 600, fontSize: "20px", color: "#000000", opacity: "87%" }}>{jobData.jobs_role.ROLE_NAME}</Typography>
                                        <Typography variant="h4" pt={2} style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>{new Date(jobData?.SERVICE_DATE).toDateString()}</Typography>
                                        <Box display="flex" gap={1} pt={2}>
                                            {jobData?.SERVICE_TYPE === "Recurring" ? <>
                                                <Box
                                                    display="grid"
                                                    gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))"
                                                    gap={2}
                                                    width="100%"
                                                >
                                                    {jobData?.SERVICE_DURATION?.map((job, index) => (
                                                        <Box
                                                            key={index}
                                                            display="flex"
                                                            alignItems="center"
                                                            width="100%"
                                                            minWidth="160px"
                                                        >
                                                            <Typography
                                                                variant="h2"
                                                                pt={1}
                                                                style={{ fontWeight: 400, fontSize: "12px", color: "#000000", opacity: "60%" }}
                                                            >
                                                                <MdOutlineWatchLater />
                                                            </Typography>
                                                            <Typography
                                                                pt={1}
                                                                px={1}
                                                                variant="h2"
                                                                style={{ fontWeight: 400, fontSize: "12px", color: "#000000", opacity: "60%" }}
                                                            >
                                                                {`${job?.JOB_DAY?.slice(0, 3)} ${job?.JOB_IN_TIME}-${job?.JOB_OUT_TIME}`}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>

                                            </> : <>
                                                <Box display="flex" gap={1}>
                                                    <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}><MdOutlineWatchLater /></Typography>
                                                    <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{new Date(jobData?.job_services[0]?.JOB_IN_TIME).toLocaleTimeString()}</Typography>  <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>-</Typography>
                                                    <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{new Date(jobData?.job_services[0]?.JOB_OUT_TIME).toLocaleTimeString()}</Typography>
                                                </Box>
                                            </>}
                                        </Box>
                                        <Typography variant="h4" pt={2} style={{ fontWeight: 500, fontSize: "14px", color: "#000000", opacity: "60%" }}>{jobData?.facility_addlocation?.STREET_ADDRESS}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            <hr style={{ marginTop: "20px", color: "#000000", opacity: "60%", width: "90%", margin: "0 auto" }} />

                            <Box display="flex" justifyContent="space-between" px={2} pt={1.2}>
                                <Typography>
                                    <Box>
                                        <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>Speciality</Typography>
                                        <Typography variant="h6" style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{jobData?.specialty?.SPECIALTY_NAME}</Typography>
                                    </Box>
                                </Typography>

                            </Box>
                            {jobData?.STAFFING_SOLUTION && <Box display="flex" justifyContent="space-between" px={2} pt={1.2} >
                                <Typography>
                                    <Box>
                                        <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>Staffing Solution</Typography>
                                        <Typography variant="h6" style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{jobData.STAFFING_SOLUTION}</Typography>
                                    </Box>
                                </Typography>

                            </Box>}
                            <Box display="flex" justifyContent="space-between" px={2} pt={1.2} >
                                <Typography>
                                    <Box>
                                        <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>Job Type</Typography>
                                        <Typography variant="h6" style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{jobData.JOB_TYPE}</Typography>
                                    </Box>
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between" px={2} pt={1.2} >
                                <Typography>
                                    <Box>
                                        <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>Positions Available</Typography>
                                        <Typography variant="h6" style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{jobData?.REQUIRED_APPLICANTS ? jobData.REQUIRED_APPLICANTS : (jobData?.REQUIRED_APPLICANTS === 0 ? '0' : 'N/A')}</Typography>
                                    </Box>
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between" px={2} pt={1.2} >
                                <Typography>
                                    <Box>
                                        <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>Start Date</Typography>
                                        <Typography variant="h6" style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{new Date(jobData?.SERVICE_DATE).toDateString()}</Typography>
                                    </Box>
                                </Typography>

                            </Box>
                            <Box display="flex" justifyContent="space-between" px={2} pt={1.2} >
                                <Typography>
                                    <Box>
                                        <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>End Date</Typography>
                                        <Typography variant="h6" style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{jobData?.SERVICE_END_DATE ? new Date(jobData.SERVICE_END_DATE).toDateString() : 'N/A'}</Typography>
                                    </Box>
                                </Typography>

                            </Box>

                            <Box display="flex" justifyContent="space-between" px={2} pt={1.2} >
                                <Typography>
                                    <Box>
                                        <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>Working Days</Typography>
                                    </Box>
                                    {/* <Box>
                                        <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>Working Days</Typography>
                                        <Button style={{ paddingLeft: "0px", textDecoration: "underline", color: "#000000", opacity: "60%" }} onClick={() => setOpen(true)}>
                                            <Tooltip title="Click here to view the Working Days" >
                                                Job Service (Date & Time)
                                                <Link>
                                                    View Working Days
                                                </Link>
                                            </Tooltip>
                                        </Button>
                                    </Box> */}

                                    {jobData?.SERVICE_DURATION
                                        ? jobData?.SERVICE_DURATION?.map((data) =>
                                            <Box display="flex" gap={1}>
                                                <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}><MdOutlineWatchLater /></Typography>
                                                <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{data?.JOB_DAY}</Typography>
                                                <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{data.JOB_IN_TIME}</Typography>  <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>-</Typography>
                                                <Typography variant="h2" pt={2} style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{data.JOB_OUT_TIME}</Typography>
                                            </Box>
                                        )

                                        :
                                        // jobData?.JOB_SERVICE_WITH_DAY_TIME &&
                                        // (<Typography variant="h6" style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>
                                        //     {jobData.JOB_SERVICE_WITH_DAY_TIME[0]?.JOB_DAY}-{jobData.JOB_SERVICE_WITH_DAY_TIME[jobData.JOB_SERVICE_WITH_DAY_TIME.length - 1]?.JOB_DAY}
                                        // </Typography>)
                                         jobData?.SERVICE_DATE && jobData?.SERVICE_END_DATE && (
    <Typography
      variant="h6"
      style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}
    >
      {new Date(jobData.SERVICE_DATE).toLocaleDateString("en-US", { weekday: "short" })}
      {" - "}
      {new Date(jobData.SERVICE_END_DATE).toLocaleDateString("en-US", { weekday: "short" })}
    </Typography>
  )
                                    }
                                </Typography>
                            </Box>
                            {jobData.SERVICE_TYPE !== "Recurring" && (
                                <Box display="flex" justifyContent="space-between" px={2} pt={1.2} >
                                    <Typography>
                                        <Box>
                                            <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }} >Time</Typography>
                                            <Typography variant="h6" style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>{jobData?.JOB_IN_TIME} - {jobData?.JOB_OUT_TIME}</Typography>
                                        </Box>
                                    </Typography>
                                </Box>
                            )}
                            <Box display="flex" justifyContent="space-between" px={2} pt={1.2}>
                                <Typography>
                                    <Box>
                                        <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>Pay</Typography>
                                        <Typography variant="h6" style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }}>${jobData.PAY_AMOUNT_FROM}-{jobData.PAY_AMOUNT_TO}/hr</Typography>
                                    </Box>
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between" px={2} pt={1.2}>
                                <Typography>
                                    <Box>
                                        <Typography variant="h4" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>Location</Typography>
                                        <Typography variant="h6" style={{ fontWeight: 400, fontSize: "14px", color: "#000000", opacity: "60%" }} >{userDetails?.STREET_ADDRESS}</Typography>
                                    </Box>
                                </Typography>
                            </Box>
                        </Card>

                    </Grid>
                    <Grid item lg={8} mt={0}>
                        <Card >
                            <Grid container alignItems="center" spacing={1} justifyContent="space-around" p={3} pb={3}>
                                <Grid item xs={12} sm={12} display="flex" justifyContent="space-between">

                                    <Typography variant="h3" style={{ fontWeight: 600, fontSize: "20px", color: "#000000", opacity: "87%" }}>
                                        Job Description
                                    </Typography>

                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Typography id="delete-modal-title" variant="h6" component="h2" style={{ fontWeight: 400, fontSize: "16px", color: "#000000", opacity: "60%" }}>
                                        <div
                                            dangerouslySetInnerHTML={{ __html: jobData?.JOB_DESCRIPION || "<p>No description provided</p>" }}
                                            style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
                                        />
                                    </Typography >
                                </Grid>
                                <Grid item xs={12} sm={12} mt={3} >

                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="h3" style={{ fontWeight: 500, fontSize: "16px", color: "#000000", opacity: "87%" }}>
                                            Responsibilities:
                                        </Typography>

                                    </Box>

                                    <Typography px={2.5} pt={1}>
                                        <div
                                            dangerouslySetInnerHTML={{ __html: jobData?.JOB_RESPONSIBILITY || "<p>No responsibilities provided</p>" }}
                                            style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
                                        />
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                </Grid>
                {open && <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        Job Services
                        <IconButton
                            aria-label="close"
                            onClick={() => setOpen(false)}
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: 8,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2} width="100%">
                            {jobData?.job_services?.length > 0 ? (
                                jobData.job_services
                                    .sort((a, b) => new Date(a.JOB_DATE) - new Date(b.JOB_DATE)) // Sorting by JOB_DATE
                                    .map((service) => (
                                        <Paper key={service.ID} sx={{
                                            p: 2,
                                            width: "calc(33.33% - 16px)", // Adjusting for 3 items per row
                                            minWidth: 250, // Prevents collapsing on small screens
                                            display: "flex",
                                            flexDirection: "column"
                                        }}
                                        >
                                            <Typography><strong>Date:</strong> {new Date(service.JOB_DATE).toDateString()}</Typography>
                                            <Typography><strong>In Time:</strong> {service.JOB_IN_TIME}</Typography>
                                            <Typography><strong>Out Time:</strong> {service.JOB_OUT_TIME}</Typography>

                                        </Paper>
                                    ))
                            ) : (
                                <Typography>No job services available.</Typography>
                            )}

                        </Box>
                    </DialogContent>
                </Dialog>}
            </>}
        </>
    )
};

export default ViewJobInFacility;

